import React, { useRef, useState } from 'react';
import styles from './index.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '../../../../components/ToastMsg';
import { addNewBanner } from '../../../../redux/features/banner/bannerService';
import BeatLoader from 'react-spinners/BeatLoader';

const BannerCreate = () => {
  const fileInputRef = useRef(null);
  const fileInputMobileRef = useRef(null);
  const dispatch = useDispatch();
  const { isBannerCreating } = useSelector((state) => state.banner.newBanner);
  const [disabledBtn, setBtnDisable] = useState(false);
  const [formData, setFormData] = useState({
    alt_text: '',
    file: null,
    file_for_mobile: null,
  });
  const [error, setError] = useState({
    alt_text: '',
    file: '',
    file_for_mobile: '',
  });

  const validateFile = (file) => {
    setFormData((prev) => ({ ...prev, file: '' }));

    if (!file.type.startsWith('image/')) {
      setError((prev) => ({ ...prev, file: 'Only Image files are allowed.' }));
      return false;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      if (img.height !== img.width / 4) {
        setError((prev) => ({
          ...prev,
          file: 'Modify the Ratio and Upload again',
        }));
      } else {
        setError((prev) => ({ ...prev, file: '' }));
        setFormData((prev) => ({ ...prev, file: file }));
      }
    };
    return true;
  };

  const validateFileMobile = (file) => {
    setFormData((prev) => ({ ...prev, file_for_mobile: '' }));

    if (!file.type.startsWith('image/')) {
      setError((prev) => ({
        ...prev,
        file_for_mobile: 'Only Image files are allowed.',
      }));
      return false;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      if (img.height !== (img.width * 2) / 3) {
        setError((prev) => ({
          ...prev,
          file_for_mobile: 'Modify the Mobile image Ratio and Upload again',
        }));
      } else {
        setError((prev) => ({ ...prev, file_for_mobile: '' }));
        setFormData((prev) => ({ ...prev, file_for_mobile: file }));
      }
    };
    return true;
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const selectedFile = event.dataTransfer.files[0];
    if (selectedFile) validateFile(selectedFile);
  };

  const handleDropMobile = (event) => {
    event.preventDefault();
    const selectedFileMobile = event.dataTransfer.files[0];
    if (selectedFileMobile) validateFileMobile(selectedFileMobile);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) validateFile(selectedFile);
  };

  const handleMobileFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) validateFileMobile(selectedFile);
  };

  const handleClick = () => fileInputRef.current.click();
  const handleClickMobile = () => fileInputMobileRef.current.click();

  const handleAltTextChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, alt_text: value }));
    setError((prev) => ({ ...prev, alt_text: '' }));
  };

  const isValidBanner = () => {
    const errors = {};
    if (!formData.file) {
      errors.file = 'Banner Image is required.';
    }
    if (!formData.file_for_mobile) {
      errors.file_for_mobile = 'Mobile Banner Image is required.';
    }
    if (!formData.alt_text.trim()) {
      errors.alt_text = 'Alt Text is required.';
    }
    if (Object.keys(errors).length > 0) {
      setError(errors);
      return false;
    }
    setError({ alt_text: '', file: '' });
    return true;
  };

  const insertNewBanner = async (e) => {
    e.preventDefault();
    if (isValidBanner()) {
      setBtnDisable(true);
      try {
        const response = await dispatch(addNewBanner(formData)).unwrap();
        setBtnDisable(false);
        setFormData({ alt_text: '', file: null });
        fileInputRef.current.value = '';
        fileInputMobileRef.current.value = '';
        showToast('success', response.message);
      } catch (error) {
        setBtnDisable(false);
        showToast('error', error.message);
      }
    }
  };

  return (
    <div className={`main-card`}>
      <h1 className={`form-title`}>New Banner</h1>
      <form noValidate>
        <div className={`form-input-row`}>
          <div className={`form-input-col`}>
            <div className={`form-input-group`}>
              <label htmlFor='alt-text' className={`form-label`}>
                Alt Text<span className={`required-star`}>*</span>
              </label>
              <input
                type='text'
                id='alt-text'
                name='alt_text'
                value={formData.alt_text}
                onChange={handleAltTextChange}
                placeholder={`Enter Alt Text`}
                className={`form-input-default`}
              />
              {error.alt_text && (
                <p className={`text-danger`}>{error.alt_text}</p>
              )}
            </div>
          </div>
        </div>
        <div className={`form-input-row`}>
          <div className={styles.bottomDiv}>
            <label htmlFor='alt-text-img' className={`form-label`}>
              Web Image Height Must be 1/4 of the Width
              <span className={`required-star`}>*</span>
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={(event) => event.preventDefault()}
              onClick={handleClick}
              className={styles.dropInput}
            >
              <input
                type='file'
                ref={fileInputRef}
                accept='image/*'
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {!formData.file ? (
                <>
                  <h2 className={styles.bannerText}>Banner Image</h2>
                  <p className={styles.dragText}>
                    Drag & Drop a file here or Click to select
                  </p>
                </>
              ) : (
                <div className={`inserted-file`}>
                  <div className={`file-card`}>
                    <img
                      src={URL.createObjectURL(formData.file)}
                      alt='Banner'
                      className={`${styles.thumbnailImg}`}
                    />
                  </div>
                </div>
              )}
            </div>
            {error.file && <p className={`text-danger`}>{error.file}</p>}
          </div>
        </div>
        <div className={`form-input-row`}>
          <div className={styles.bottomDiv}>
            <label htmlFor='alt-text-img' className={`form-label`}>
              Mobile Image Height Must be 2/3 of the Width
              <span className={`required-star`}>*</span>
            </label>
            <div
              onDrop={handleDropMobile}
              onDragOver={(event) => event.preventDefault()}
              onClick={handleClickMobile}
              className={styles.dropInputMobile}
            >
              <input
                type='file'
                ref={fileInputMobileRef}
                accept='image/*'
                onChange={handleMobileFileChange}
                style={{ display: 'none' }}
              />
              {!formData.file_for_mobile ? (
                <>
                  <h2 className={styles.bannerText}>Mobile Banner Image</h2>
                  <p className={styles.dragText}>
                    Drag & Drop a file here or Click to select
                  </p>
                </>
              ) : (
                <div className={`inserted-file`}>
                  <div className={`file-card`}>
                    <img
                      src={URL.createObjectURL(formData.file_for_mobile)}
                      alt='Mobile Banner'
                      className={`${styles.thumbnailImgMobile}`}
                    />
                  </div>
                </div>
              )}
            </div>
            {error.file_for_mobile && (
              <p className={`text-danger`}>{error.file_for_mobile}</p>
            )}
          </div>
        </div>
        <button
          className={`btn-primary`}
          type='button'
          disabled={disabledBtn}
          onClick={insertNewBanner}
        >
          {isBannerCreating ? <BeatLoader banner='#ffffff' /> : `Create`}
        </button>
      </form>
    </div>
  );
};

export default BannerCreate;
